import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../component/appLayout';
import {
  BorderDiv,
  FirstContent,
  LeftContent,
  Main,
  MainContent,
  RightContent,
  TimeTitle,
  TopicDescription,
  TopicTitle
} from './styles';
import { loadEditTimeLog, loadMyTimeLog } from '../../services/timeTrackingServices';
import { SelectRangeInterface } from '../../interfaces/ReportInterface';
import { getDateRange, getDayFromDate, getMonthRange, isEmpty } from '../../helpers/common';
import { getUserWorkspacePreferenceData } from '../../helpers/firebaseHelper';
import dayjs from 'dayjs';
import { ProjectDetailInterface } from '../../interfaces/ProjectInterface';
import { TimeLogInterface } from '../../interfaces/TimeReportInterface';
import { TimeEntryInputInterface } from '../../interfaces/TimeTrackingInterface';
import ReportTimelineLoading from '../../component/loading/reportTimeLineLoading';
import AddNewTimeModal from '../../component/modals/addNewTimeModal';
import Modal from '../../component/modals/modal';
import MyTimelogHeader from './myTimelogHeader';
import ResponsiveHeader from '../../component/responsiveHeader';
import { useMobileDevice } from '../../global/useMobile';
import BottomSubNavigation from '../../component/bottomNavigation/subNavigation';
import { ProjectModel } from '../../mobx/models/project';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import IconButton from '../../component/button/iconButton';
import { EMPTY_STATE_SIZE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import EmptyStateV2 from '../../component/emptyState';

const MyTimelog: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [totalTime, setTotalTime] = useState('0 hours 0 minutes');
  const [filterLogList, setFilterLogList] = useState<TimeLogInterface[]>([]);
  const [open, setOpen] = useState(false);

  // Mobx variables
  const { projectStore, workspaceStore, timeTrackingStore } = rootStore;
  const { projects } = projectStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const { myTimeLog, setMyTimeLogFilter, setTimeEntryInput, clearTimeEntryInput } = timeTrackingStore;
  // Other variables
  const mobile = useMobileDevice();

  // for calculating total time
  const calculateTotalTime = useCallback((list: TimeLogInterface[]) => {
    const total = list?.reduce((acc: number, item: TimeLogInterface) => {
      return acc + (item?.hours || 0);
    }, 0);
    const timeString = convertDecimalToHoursMinutes(total, true);
    setTotalTime(timeString);
  }, []);

  // implement filter logic
  const applyFilter = useCallback(
    async (
      projects: ProjectDetailInterface[],
      selectionRange: SelectRangeInterface,
      myTimeReportData?: TimeLogInterface[]
    ) => {
      const selectedProjects = projects?.filter((x) => x?.checked);
      const selectedProjectIds = selectedProjects?.map((x) => x?.id);
      if (!myTimeReportData) myTimeReportData = myTimeLog;
      let timeLogData: TimeLogInterface[] = JSON.parse(JSON.stringify(myTimeReportData));
      const startDate = dayjs(selectionRange.startDate);
      const endDate = dayjs(selectionRange.endDate).add(1, 'day');
      timeLogData = timeLogData?.filter(
        (item) =>
          selectedProjectIds?.includes(item?.projectId) &&
          dayjs(item?.date) >= startDate &&
          dayjs(item?.date) <= endDate
      );
      const formattedTimeLogData = timeLogData?.map((item) => ({
        ...(item || {}),
        day: getDayFromDate(item?.date)
      }));
      setFilterLogList(formattedTimeLogData || []);
      calculateTotalTime(timeLogData || []);
    },
    [calculateTotalTime, myTimeLog]
  );

  // initial load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (!workspace?.id) return;
      setFilterLogList([]);
      // Get my timelog filter from user preference
      const result = await getUserWorkspacePreferenceData(`my_timelog_filter`);
      const monthRange = getMonthRange(result?.selected_range?.startDate, result?.selected_range?.endDate);
      const logList = await loadMyTimeLog(workspace?.id, monthRange);
      const updatedProjectsResponse = projects?.map((item: ProjectModel) => {
        return { id: item?.id, checked: true };
      });

      const hasNewProjects =
        !result?.project_list ||
        updatedProjectsResponse.some(
          (project: { id: string }) => !result.project_list.some((p: { id: string }) => p.id === project?.id)
        );

      const updatedDateRange = result?.selected_range
        ? {
            startDate: new Date(result?.selected_range?.startDate).toISOString(),
            endDate: new Date(result?.selected_range?.endDate).toISOString(),
            key: 'selection'
          }
        : {
            startDate: new Date(new Date().setHours(0, 0, 0, 0) - 330 * 60 * 1000).toISOString(),
            endDate: new Date(new Date().setHours(0, 0, 0, 0) - 330 * 60 * 1000).toISOString(),
            key: 'selection'
          };
      let data = {
        ...(result || {}),
        project_list: hasNewProjects ? updatedProjectsResponse : result?.project_list,
        selected_text: result?.selected_text || 'Custom',
        selected_range: updatedDateRange,
        selected_all_project: isEmpty(result?.selected_all_project) ? true : result?.selected_all_project
      };

      // set date range based on text
      if (data.selected_text !== 'Custom') {
        data = {
          ...(data || {}),
          selected_range: getDateRange(data.selected_text)
        };
      }
      setMyTimeLogFilter(data);

      await applyFilter(
        !data?.project_list ? projects || [] : data?.project_list,
        !data?.selected_range
          ? {
              startDate: new Date(),
              endDate: new Date(),
              key: 'selection'
            }
          : {
              startDate: new Date(data?.selected_range?.startDate),
              endDate: new Date(data?.selected_range?.endDate),
              key: data?.selected_range?.key
            },
        logList as unknown as TimeLogInterface[]
      );
    } catch (error) {
      console.log('error', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // open update modal
  const handleOpenUpdateModal = useCallback(
    async (item: TimeLogInterface) => {
      setTimeEntryInput(item as unknown as Partial<TimeEntryInputInterface>);
      await loadEditTimeLog();
      setOpen(true);
    },
    [setTimeEntryInput]
  );

  // close add modal
  const closeAddModal = useCallback(() => {
    setOpen(false);
    clearTimeEntryInput();
  }, [clearTimeEntryInput]);

  // convert decimal to hours and minutes
  const convertDecimalToHoursMinutes = (decimalHours: number, isFullString: boolean, withoutInitials?: boolean) => {
    const hours = Math.floor(decimalHours); // Get the integer part (hours)
    const minutes = Math.round((decimalHours - hours) * 60); // Convert decimal to minutes
    return isFullString
      ? `${hours} hours ${minutes} minutes`
      : withoutInitials
      ? `${hours} : ${minutes}`
      : `${hours}h ${minutes}m`;
  };

  // render filter log list
  const renderFilterLogList = useMemo(() => {
    return filterLogList?.map((item: TimeLogInterface, index) => {
      const time = convertDecimalToHoursMinutes(item?.hours, false);
      return (
        <FirstContent key={`${item.date + item.taskId}`}>
          <LeftContent>
            <TopicTitle>{item?.projectName}</TopicTitle>
            <TopicDescription>{item?.taskName}</TopicDescription>
          </LeftContent>
          <RightContent>
            <TimeTitle>
              <span>{time}</span>
            </TimeTitle>
            <BorderDiv />
            {/* need to add functionality */}
            {item.isInvoiced ? (
              <IconButton
                iconName='lock-icon'
                size={ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                className='lock-icon'
                iconColorType='primary'
              />
            ) : (
              <IconButton
                iconName='edit-icon'
                size={ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
                onClick={() => handleOpenUpdateModal(item)}
              />
            )}
          </RightContent>
        </FirstContent>
      );
    });
  }, [filterLogList, handleOpenUpdateModal]);

  const renderHeader = useMemo(() => {
    return (
      <MyTimelogHeader
        loading={loading}
        setLoading={setLoading}
        setTotalTime={setTotalTime}
        setFilterLogList={setFilterLogList}
        totalTime={totalTime}
        filterLogList={filterLogList}
      />
    );
  }, [filterLogList, loading, totalTime]);

  return (
    <>
      <AppLayout>
        {!mobile && renderHeader}
        <Main
          className={
            !loading && filterLogList?.length > 0
              ? 'desktop-your-time-log-content'
              : 'desktop-your-time-log-content-empty'
          }>
          {mobile && (
            <>
              <ResponsiveHeader title='Your Timelog' />
              {renderHeader}
            </>
          )}
          {!loading && filterLogList?.length > 0 && <MainContent>{renderFilterLogList}</MainContent>}
          {loading && filterLogList?.length === 0 && <ReportTimelineLoading />}
          {!loading && filterLogList?.length === 0 && (
            <EmptyStateV2
              size={EMPTY_STATE_SIZE.MEDIUM}
              title={`You don't have time log data`}
              description={'There are no time log data for this date. Please try another Filters.'}
              imageUrl={'reports_empty'}
              isAlignedCenter={true}
            />
          )}
        </Main>
      </AppLayout>
      <Modal open={open} onClose={closeAddModal} maxWidth='520px'>
        <AddNewTimeModal onCloseModal={closeAddModal} loadData={loadData} isMyTimeLogModal={true} />
      </Modal>
      <BottomSubNavigation />
    </>
  );
};

export default observer(MyTimelog);
